import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSelectedHousehold } from '../hooks/useSelectedHousehold';
import { useHouseholdMembers } from '../hooks/useHousehold';
import { useChoreAssignments } from '../hooks/useChoreAssignments';
import { useDailyStats } from '../hooks/useUserPoints';
import { MemberAvatar } from '../components/MemberAvatar';
import { AssignmentStatus, ChoreLength, ChorePriority } from '@chore-project/shared';

const POINTS_TABLE: Record<ChorePriority, Record<ChoreLength, number>> = {
  [ChorePriority.MUST_DO]:   { [ChoreLength.SHORT]: 4, [ChoreLength.MEDIUM]: 5, [ChoreLength.LONG]: 7 },
  [ChorePriority.SHOULD_DO]: { [ChoreLength.SHORT]: 3, [ChoreLength.MEDIUM]: 4, [ChoreLength.LONG]: 6 },
  [ChorePriority.CAN_DO]:    { [ChoreLength.SHORT]: 2, [ChoreLength.MEDIUM]: 3, [ChoreLength.LONG]: 5 },
};

function calcPoints(priority: ChorePriority, length: ChoreLength): number {
  return POINTS_TABLE[priority]?.[length] ?? 0;
}

const PRIORITY_DOT: Record<ChorePriority, string> = {
  [ChorePriority.MUST_DO]:   'oklch(52% 0.19 25)',
  [ChorePriority.SHOULD_DO]: 'oklch(62% 0.16 50)',
  [ChorePriority.CAN_DO]:    'oklch(55% 0.03 72)',
};

const LENGTH_LABEL: Record<ChoreLength, string> = {
  [ChoreLength.SHORT]:  '~10m',
  [ChoreLength.MEDIUM]: '~30m',
  [ChoreLength.LONG]:   '45m+',
};

// Representative minutes per length, used to estimate remaining chore time.
const LENGTH_MINUTES: Record<ChoreLength, number> = {
  [ChoreLength.SHORT]:  10,
  [ChoreLength.MEDIUM]: 30,
  [ChoreLength.LONG]:   45,
};

// Length is optional on a chore; treat a missing one as MEDIUM everywhere it's
// shown or summed, matching how the dashboard and points already behave.
const lengthOf = (length: ChoreLength | undefined): ChoreLength => length ?? ChoreLength.MEDIUM;

function formatDuration(mins: number): string {
  if (mins < 60) return `~${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `~${h}h` : `~${h}h ${m}m`;
}

const REFETCH_INTERVAL = 5 * 60 * 1000;
const GREEN      = 'oklch(62% 0.14 145)';
const GREEN_DIM  = 'oklch(52% 0.14 145)';
const RED        = 'oklch(58% 0.22 25)';
// Theme-aware tokens so the Home View follows light/dark like the rest of the app.
const BORDER     = 'var(--border)';
const MUTED      = 'var(--text-muted)';

export function HomeViewPage() {
  const queryClient = useQueryClient();
  const { householdId, households, setSelectedId } = useSelectedHousehold();
  const today = new Date().toISOString().split('T')[0] ?? '';
  const yesterday = new Date(Date.now() - 86_400_000).toISOString().split('T')[0] ?? '';

  const { data: members = [] } = useHouseholdMembers(householdId);
  // The Home View only needs today's chores plus the overdue *open* backlog
  // (PENDING or SHARED). Fetching those two scoped windows — rather than the
  // household's entire assignment history — keeps the payload small and flat for
  // long-lived households.
  const { data: todayAssignmentsData = [] } = useChoreAssignments(householdId, { from: today, to: today });
  const { data: overdueAssignmentsData = [] } = useChoreAssignments(householdId, {
    to: yesterday,
    status: [AssignmentStatus.PENDING, AssignmentStatus.SHARED],
  });
  const { data: dailyStats = [] } = useDailyStats(householdId, today);

  // Counts are scoped to current members (so chores left behind by a removed member
  // don't inflate totals) and to actionable statuses. "Open" = PENDING or SHARED;
  // skipped/missed chores are excluded so they don't read as still-to-do.
  const memberIds = useMemo(() => new Set(members.map((m) => m.userId)), [members]);
  const isOpen = (s: AssignmentStatus) => s === AssignmentStatus.PENDING || s === AssignmentStatus.SHARED;

  // Today: member-assigned, excluding skipped (MISSED) — keeps PENDING/SHARED/COMPLETED.
  const todayAssignments = useMemo(
    () => todayAssignmentsData.filter((a) => memberIds.has(a.assigneeId) && a.status !== AssignmentStatus.MISSED),
    [todayAssignmentsData, memberIds],
  );
  // Overdue: member-assigned, still open, strictly before today.
  const overdueAssignments = useMemo(
    () => overdueAssignmentsData.filter((a) => memberIds.has(a.assigneeId) && isOpen(a.status) && a.dueDate < today),
    [overdueAssignmentsData, memberIds, today],
  );

  const [time, setTime] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!householdId) return;
    const id = setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: ['households', householdId, 'members'] });
      void queryClient.invalidateQueries({ queryKey: ['chore-assignments', householdId] });
      void queryClient.invalidateQueries({ queryKey: ['points', 'daily', householdId] });
    }, REFETCH_INTERVAL);
    return () => clearInterval(id);
  }, [householdId, queryClient]);

  const currentHousehold = households?.find((h) => h.householdId === householdId);

  const totalDone    = todayAssignments.filter((a) => a.status === AssignmentStatus.COMPLETED).length;
  const totalOverdue = overdueAssignments.length;
  const totalAll     = todayAssignments.length + totalOverdue;
  const householdFillPct = totalAll > 0 ? Math.round((totalDone / totalAll) * 100) : 0;
  const householdTotalPoints = dailyStats.reduce((sum, s) => sum + s.dailyPoints, 0);

  const heroStat = dailyStats.reduce<{ userId: string; pts: number } | null>((best, s) => {
    if (s.dailyPoints > (best?.pts ?? 0)) return { userId: s.userId, pts: s.dailyPoints };
    return best;
  }, null);
  const heroId     = heroStat && heroStat.pts > 0 ? heroStat.userId : null;
  const heroMember = heroId ? members.find((m) => m.userId === heroId) : null;

  const dateLabel = time.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
  const timeLabel = time.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  return (
    <div style={{ minHeight: '100dvh', background: 'var(--bg)', backgroundImage: 'var(--bg-grad)', display: 'flex', flexDirection: 'column', paddingTop: 'var(--sat)', paddingBottom: 'var(--sab)', paddingLeft: 'var(--sal)', paddingRight: 'var(--sar)' }}>

      {/* ── Header ── */}
      <div style={{
        display: 'flex', alignItems: 'center',
        padding: '0.875rem 1.75rem', borderBottom: `1px solid ${BORDER}`,
        gap: '1rem', flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', color: 'var(--primary)', flexShrink: 0 }}>
            <ion-icon name="home-outline" style={{ fontSize: '1rem' }} />
          </Link>
          {households && households.length > 1 ? (
            <select
              value={householdId}
              onChange={(e) => setSelectedId(e.target.value)}
              style={{
                fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.125rem',
                color: 'var(--text)', background: 'transparent', border: 'none',
                outline: 'none', cursor: 'pointer', padding: 0, appearance: 'auto',
              }}
            >
              {households.map((h) => (
                <option key={h.householdId} value={h.householdId}>{h.name}</option>
              ))}
            </select>
          ) : (
            <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.125rem', color: 'var(--text)' }}>
              {currentHousehold?.name ?? 'Home View'}
            </span>
          )}
        </div>

        <div style={{ textAlign: 'right', lineHeight: 1, marginLeft: 'auto' }}>
          <div style={{ fontSize: '0.6875rem', color: MUTED, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '0.2rem' }}>
            {dateLabel}
          </div>
          <div style={{
            fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: 800,
            color: 'var(--text)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.03em',
          }}>
            {timeLabel}
          </div>
        </div>
      </div>

      {/* ── Overview scoreboard banner ── */}
      {heroMember && heroStat ? (
        /* Two-zone: hero spotlight (60%) | progress summary (40%) */
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', borderBottom: `1px solid ${BORDER}` }}>

          {/* Hero zone */}
          <div style={{
            flex: '3 1 260px', position: 'relative',
            display: 'flex', alignItems: 'center', gap: '1.5rem',
            padding: '1.75rem 2rem 1.75rem 2.25rem',
            borderRight: `1px solid ${BORDER}`,
          }}>
            {/* Accent stripe — hero's personal color */}
            <div style={{
              position: 'absolute', left: 0, top: 0, bottom: 0, width: '5px',
              background: heroMember.avatarColor ?? 'var(--primary)',
            }} />

            {/* Avatar + crown (animated — matches the "Hero today" crown elsewhere) */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div style={{ borderRadius: '50%', boxShadow: `0 0 0 3px ${BORDER}` }}>
                <MemberAvatar member={heroMember} size="5rem" fontSize="2rem" />
              </div>
              <span className="hero-spotlight-emblem" style={{ fontSize: '2rem' }}>👑</span>
            </div>

            {/* Hero text */}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: MUTED, marginBottom: '0.25rem' }}>
                Hero Today
              </div>
              <div style={{
                fontFamily: 'var(--font-display)', fontWeight: 800,
                fontSize: 'clamp(1.625rem, 3vw, 2.375rem)',
                color: 'var(--text)', letterSpacing: '-0.03em', lineHeight: 1,
                marginBottom: '0.5rem',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {heroMember.memberName || heroMember.email}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.375rem' }}>
                <span style={{
                  fontFamily: 'var(--font-display)', fontSize: '1.75rem', fontWeight: 800,
                  color: heroMember.avatarColor ?? 'var(--primary)',
                  letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums',
                }}>
                  {heroStat.pts}
                </span>
                <span style={{ fontSize: '0.875rem', color: MUTED }}>pts earned</span>
              </div>
            </div>
          </div>

          {/* Progress zone */}
          <div style={{
            flex: '2 1 200px', padding: '1.5rem 1.75rem',
            display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '0.625rem',
          }}>
            <div style={{ fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: MUTED }}>
              Today's progress
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.3rem' }}>
              <span style={{
                fontFamily: 'var(--font-display)', fontWeight: 800,
                fontSize: 'clamp(2.25rem, 3.5vw, 3rem)',
                color: householdFillPct === 100 ? GREEN : 'var(--text)',
                letterSpacing: '-0.04em', lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
              }}>
                {totalDone}
              </span>
              <span style={{ fontSize: '0.9375rem', color: MUTED }}> / {totalAll}</span>
              <span style={{ fontSize: '0.8125rem', color: MUTED }}>done</span>
            </div>
            <div style={{ height: '8px', background: BORDER, borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{
                height: '100%', width: `${householdFillPct}%`,
                background: householdFillPct === 100 ? GREEN : 'var(--primary)',
                borderRadius: '4px', transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)',
              }} />
            </div>
            <div style={{ display: 'flex', gap: '0.875rem', fontSize: '0.8125rem', flexWrap: 'wrap', alignItems: 'center' }}>
              {totalAll === 0 ? (
                <span style={{ color: MUTED }}>No chores today</span>
              ) : householdFillPct === 100 ? (
                <span style={{ color: GREEN, fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <ion-icon name="checkmark-done" style={{ fontSize: '0.875rem' }} /> All done!
                </span>
              ) : (
                <span style={{ color: MUTED }}>{totalAll - totalDone} remaining</span>
              )}
              {totalOverdue > 0 && (
                <span style={{ color: RED, display: 'flex', alignItems: 'center', gap: '0.25rem', fontWeight: 600 }}>
                  <ion-icon name="warning" style={{ fontSize: '0.8125rem' }} /> {totalOverdue} overdue
                </span>
              )}
              {householdTotalPoints > 0 && (
                <span style={{ color: 'var(--primary)', fontWeight: 600 }}>{householdTotalPoints} pts</span>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* No hero — full-width progress */
        <div style={{
          display: 'flex', gap: '3rem', alignItems: 'center',
          padding: '1.5rem 1.75rem', borderBottom: `1px solid ${BORDER}`,
          flexWrap: 'wrap',
        }}>
          <div style={{ flex: '2 1 260px' }}>
            <div style={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: MUTED, marginBottom: '0.5rem' }}>
              Today's progress
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem', marginBottom: '0.875rem' }}>
              <span style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(2.75rem, 5vw, 4.5rem)',
                fontWeight: 800, lineHeight: 1,
                color: householdFillPct === 100 ? GREEN : 'var(--text)',
                letterSpacing: '-0.04em',
              }}>
                {totalDone}
              </span>
              <span style={{ fontSize: '1.25rem', color: MUTED, fontWeight: 500 }}>
                / {totalAll} done
              </span>
            </div>
            <div style={{ height: '10px', background: BORDER, borderRadius: '5px', overflow: 'hidden', marginBottom: '0.75rem' }}>
              <div style={{
                height: '100%', width: `${householdFillPct}%`,
                background: householdFillPct === 100 ? GREEN : 'var(--primary)',
                borderRadius: '5px', transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)',
              }} />
            </div>
            <div style={{ display: 'flex', gap: '1.25rem', fontSize: '0.875rem', alignItems: 'center', flexWrap: 'wrap' }}>
              {totalAll === 0 ? (
                <span style={{ color: MUTED }}>No chores assigned today</span>
              ) : householdFillPct === 100 ? (
                <span style={{ color: GREEN, fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <ion-icon name="checkmark-done" style={{ fontSize: '1rem' }} /> All done!
                </span>
              ) : (
                <span style={{ color: MUTED }}>{totalAll - totalDone} remaining</span>
              )}
              {totalOverdue > 0 && (
                <span style={{ color: RED, display: 'flex', alignItems: 'center', gap: '0.3rem', fontWeight: 600 }}>
                  <ion-icon name="warning" style={{ fontSize: '0.9rem' }} />
                  {totalOverdue} overdue
                </span>
              )}
              {householdTotalPoints > 0 && (
                <span style={{ color: 'var(--primary)', marginLeft: 'auto', fontWeight: 600 }}>
                  {householdTotalPoints} pts earned today
                </span>
              )}
            </div>
          </div>
          <div style={{ flex: '1 1 160px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', textAlign: 'center' }}>
            <span style={{ fontSize: '2.25rem', lineHeight: 1, opacity: 0.4, filter: 'grayscale(0.3)' }}>👑</span>
            <span style={{ fontSize: '0.875rem', color: MUTED }}>No hero yet</span>
          </div>
        </div>
      )}

      {/* ── Member row ── */}
      <div style={{
        display: 'flex', gap: '1.25rem',
        padding: '1.5rem 1.75rem',
        overflowX: 'auto', flex: 1, alignItems: 'stretch',
        WebkitOverflowScrolling: 'touch' as never,
      }}>
        {members.length === 0 && (
          <p style={{ color: MUTED }}>No members in this household.</p>
        )}

        {members.map((member) => {
          const memberToday   = todayAssignments.filter((a) => a.assigneeId === member.userId);
          const memberOverdue = overdueAssignments.filter((a) => a.assigneeId === member.userId);
          const earnedPoints  = dailyStats.find((s) => s.userId === member.userId)?.dailyPoints ?? 0;
          const maxPoints     = memberToday.reduce(
            (sum, a) => sum + calcPoints(a.priority ?? ChorePriority.SHOULD_DO, lengthOf(a.length)),
            0,
          );
          // Open = still to do (PENDING) or passed to a housemate awaiting accept (SHARED).
          const openToday      = memberToday.filter((a) => a.status === AssignmentStatus.PENDING || a.status === AssignmentStatus.SHARED);
          const openCount      = openToday.length;
          const completedCount = memberToday.filter((a) => a.status === AssignmentStatus.COMPLETED).length;
          const allDone        = memberToday.length > 0 && memberOverdue.length === 0 && openCount === 0;
          const isHero         = member.userId === heroId;

          // Estimated time left = sum of this member's remaining chores (today's
          // open ones + overdue), defaulting a missing length to MEDIUM.
          const remainingMins  = [...openToday, ...memberOverdue]
            .reduce((sum, a) => sum + LENGTH_MINUTES[lengthOf(a.length)], 0);

          const sorted = [
            ...memberOverdue,
            ...openToday,
            ...memberToday.filter((a) => a.status === AssignmentStatus.COMPLETED),
          ];
          const MAX_SHOWN = 10;
          const overflow  = sorted.length - MAX_SHOWN;
          const shown     = sorted.slice(0, MAX_SHOWN);

          const fillPct     = maxPoints > 0 ? Math.min(100, (earnedPoints / maxPoints) * 100) : 0;
          const name        = member.memberName || member.email;
          const accentColor = member.avatarColor ?? 'var(--primary)';

          let statusText: string;
          let statusBg: string;
          let statusFg: string;
          if (allDone) {
            statusText = 'All done!';
            statusBg   = 'oklch(62% 0.14 145 / 0.15)';
            statusFg   = GREEN_DIM;
          } else if (memberOverdue.length > 0) {
            statusText = `${memberOverdue.length} overdue`;
            statusBg   = 'oklch(94% 0.06 25)';
            statusFg   = 'oklch(42% 0.18 25)';
          } else if (completedCount > 0 && openCount > 0) {
            statusText = `${completedCount} / ${completedCount + openCount} done`;
            statusBg   = 'oklch(96% 0.04 62)';
            statusFg   = 'oklch(40% 0.10 62)';
          } else if (openCount > 0) {
            statusText = `${openCount} to do`;
            statusBg   = 'oklch(96% 0.04 62)';
            statusFg   = 'oklch(40% 0.10 62)';
          } else {
            statusText = 'No chores today';
            statusBg   = 'oklch(95% 0.015 240)';
            statusFg   = 'oklch(48% 0.03 240)';
          }

          return (
            <div
              key={member.userId}
              style={{
                flex: '1 1 280px', minWidth: '280px', maxWidth: '420px', flexShrink: 0,
                background: allDone
                  ? 'color-mix(in oklch, var(--surface) 88%, oklch(62% 0.14 145) 12%)'
                  : 'var(--surface)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--shadow-lg)',
                overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
              }}
            >
              {/* ── Color cap ── */}
              <div style={{ height: '72px', background: accentColor, position: 'relative', flexShrink: 0 }}>
                {isHero ? (
                  <span style={{
                    position: 'absolute', top: '0.5rem', right: '0.75rem',
                    fontSize: '1.5rem', lineHeight: 1,
                    filter: 'drop-shadow(0 1px 2px oklch(0% 0 0 / 0.4))',
                  }}>👑</span>
                ) : allDone ? (
                  <ion-icon
                    name="checkmark-done"
                    style={{
                      position: 'absolute', top: '0.625rem', right: '0.875rem',
                      fontSize: '1.375rem', color: 'rgba(255,255,255,0.85)',
                    }}
                  />
                ) : null}
              </div>

              {/* ── Avatar (overlaps cap) ── */}
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '-2rem', position: 'relative', zIndex: 1, flexShrink: 0 }}>
                <div style={{ borderRadius: '50%', boxShadow: '0 0 0 4px var(--surface)' }}>
                  <MemberAvatar member={member} size="4rem" fontSize="1.625rem" />
                </div>
              </div>

              {/* ── Card body ── */}
              <div style={{ padding: '0.625rem 1.375rem 1.375rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', flex: 1 }}>

                {/* Name */}
                <div style={{ textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.1875rem', color: 'var(--text)', letterSpacing: '-0.01em' }}>
                    {name}
                  </span>
                </div>

                {/* Status pill */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <span style={{
                    padding: '0.2rem 0.875rem', borderRadius: '9999px',
                    fontSize: '0.75rem', fontWeight: 600,
                    background: statusBg, color: statusFg,
                    display: 'flex', alignItems: 'center', gap: '0.25rem',
                  }}>
                    {allDone && <ion-icon name="checkmark-done" style={{ fontSize: '0.8125rem' }} />}
                    {memberOverdue.length > 0 && !allDone && <ion-icon name="warning" style={{ fontSize: '0.8125rem' }} />}
                    {statusText}
                  </span>
                </div>

                {/* Estimated time remaining */}
                {remainingMins > 0 && (
                  <div style={{
                    display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.3rem',
                    fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '-0.4rem',
                  }}>
                    <ion-icon name="time-outline" style={{ fontSize: '0.875rem' }} />
                    <span>{formatDuration(remainingMins)} of chores left</span>
                  </div>
                )}

                {/* Points — always shown */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.3rem', marginBottom: '0.4rem' }}>
                    <span style={{
                      fontFamily: 'var(--font-display)', fontSize: '2.375rem', fontWeight: 800,
                      lineHeight: 1, letterSpacing: '-0.03em',
                      color: earnedPoints > 0 ? accentColor : 'var(--border)',
                    }}>
                      {earnedPoints}
                    </span>
                    <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                      {maxPoints > 0 ? `/ ${maxPoints} pts` : 'pts'}
                    </span>
                  </div>
                  <div style={{ height: '8px', borderRadius: '4px', background: 'var(--border)', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', width: `${fillPct}%`,
                      background: allDone ? GREEN : accentColor,
                      borderRadius: '4px', transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)',
                    }} />
                  </div>
                </div>

                {/* Divider */}
                <div style={{ borderTop: '1px solid var(--border)', margin: '0.125rem -1.375rem' }} />

                {/* Chore list */}
                {shown.length === 0 ? (
                  <p style={{ fontSize: '0.9375rem', color: 'var(--text-muted)', margin: 0, textAlign: 'center' }}>
                    No chores today
                  </p>
                ) : (
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {shown.map((a) => {
                      const isOverdue = a.dueDate < today && a.status === AssignmentStatus.PENDING;
                      const isDone    = a.status === AssignmentStatus.COMPLETED;
                      const priority  = a.priority ?? ChorePriority.SHOULD_DO;
                      const length    = lengthOf(a.length);
                      return (
                        <li
                          key={a.assignmentId}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '0.5rem',
                            fontSize: '0.9375rem',
                            color: isOverdue ? RED : isDone ? 'var(--text-muted)' : 'var(--text)',
                          }}
                        >
                          <ion-icon
                            name={isDone ? 'checkmark-circle' : isOverdue ? 'warning' : 'ellipse-outline'}
                            style={{
                              fontSize: '1rem', flexShrink: 0,
                              color: isDone ? GREEN : isOverdue ? RED : 'var(--text-muted)',
                            }}
                          />
                          <span style={{
                            flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            textDecoration: isDone ? 'line-through' : 'none',
                          }}>
                            {a.choreName}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
                            <div style={{
                              width: '6px', height: '6px', borderRadius: '50%',
                              background: PRIORITY_DOT[priority],
                              opacity: isDone ? 0.35 : 1,
                            }} />
                            <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', opacity: isDone ? 0.5 : 1 }}>
                              {LENGTH_LABEL[length]}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                    {overflow > 0 && (
                      <li style={{ fontSize: '0.75rem', color: 'var(--text-muted)', paddingLeft: '1.5rem' }}>
                        +{overflow} more
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
